/**
 * Mobile screen detection utility
 *
 * This module provides functions to detect if the current device is a mobile device
 * based on screen size and/or user agent.
 */

import { useEffect, useState } from 'react';

/**
 * Checks if the current screen width is below the specified breakpoint
 * @param {number} breakpoint - The maximum width in pixels to consider as mobile (default: 768)
 * @returns {boolean} - True if screen is narrower than the breakpoint
 */
export const isMobile = (breakpoint = 768) => {
  if (typeof window === 'undefined') return false; // SSR check
  return window.innerWidth < breakpoint;
};

/**
 * Creates an event listener that triggers a callback when mobile status changes
 * @param {function} callback - Function to call when mobile status changes, receives isMobile boolean
 * @param {number} breakpoint - The maximum width in pixels to consider as mobile (default: 768)
 * @returns {function} - Function to remove the event listener
 */
export const onMobileChange = (callback: any, breakpoint = 768) => {
  if (typeof window === 'undefined') return () => {}; // SSR check

  let lastValue = isMobile(breakpoint);

  const handleResize = () => {
    const newValue = isMobile(breakpoint);
    if (newValue !== lastValue) {
      lastValue = newValue;
      callback(newValue);
    }
  };

  window.addEventListener('resize', handleResize);

  // Return function to remove the listener
  return () => {
    window.removeEventListener('resize', handleResize);
  };
};

/**
 * Hook for React components to detect mobile status
 *
 * @param {number} breakpoint - The maximum width in pixels to consider as mobile (default: 768)
 * @returns {boolean} - True if the current device is mobile
 */
export const useIsMobile = (breakpoint = 600) => {
  const [mobile, setMobile] = useState(isMobile(breakpoint));

  useEffect(() => {
    const cleanup = onMobileChange((isMobile: boolean) => {
      setMobile(isMobile);
    }, breakpoint);

    return cleanup;
  }, [breakpoint]);

  return mobile;
};

// Default export for convenience
export default isMobile;
